import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import {
  Search, BrainCircuit, Workflow, Gauge, Sparkles, Target,
  ShieldCheck, Layers, ArrowUpRight, Building2, HeartPulse,
  Cpu, Landmark, Rocket, Scale, Users,
} from "lucide-react";
import { Reveal, Eyebrow, EASE } from "./primitives";
import { WaitlistForm } from "./WaitlistForm";

const IMG = {
  network: "https://images.unsplash.com/photo-1737505599162-d9932323a889?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  waves: "https://images.pexels.com/photos/12306417/pexels-photo-12306417.jpeg?auto=compress&cs=tinysrgb&w=1400",
  grid: "https://images.unsplash.com/photo-1561233835-f937539b95b9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  plexus: "https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  lines: "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const container = "mx-auto max-w-7xl px-6";

/* ---------------- Built by Recruiters ---------------- */
export const BuiltByRecruiters = () => {
  const stats = [
    { k: "8 hrs → 12 min", v: "Average sourcing time per role" },
    { k: "3.4×", v: "More qualified candidates surfaced" },
    { k: "40+", v: "Data signals analyzed per profile" },
    { k: "100%", v: "Built alongside working recruiters" },
  ];
  return (
    <section className={`${container} py-24 sm:py-32`} data-testid="built-by-recruiters">
      <div className="grid gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
        <Reveal>
          <Eyebrow>Built by recruiters</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
            Made by people who have
            <span className="italic text-electric"> lived the pipeline.</span>
          </h2>
          <p className="mt-6 max-w-md font-body text-base leading-relaxed text-muted-foreground">
            Kyvelra was shaped by staffing veterans and sourcing leads who were
            tired of tab-hopping across ten tools. Every feature answers a real
            recruiting problem — not a demo checklist.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-border bg-border">
          {stats.map((s, i) => (
            <Reveal key={s.k} delay={i * 0.08} className="bg-background">
              <div className="p-7 sm:p-9">
                <div className="font-serif text-3xl tracking-tight text-navy-950 sm:text-4xl">
                  {s.k}
                </div>
                <div className="mt-2 font-display text-sm text-muted-foreground">{s.v}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Editorial Marquee ---------------- */
export const EditorialMarquee = () => (
  <section className="border-y border-border bg-navy-950 py-8" data-testid="marquee">
    <Marquee speed={38} gradient={false} autoFill>
      {["Candidate Discovery", "Sourcing Strategy", "Recruiting Research", "Talent Intelligence", "Boolean, Automated", "Outreach Signals"].map((t, i) => (
        <span key={i} className="mx-8 flex items-center gap-8">
          <span className="font-serif text-4xl italic text-electric-ice/85 sm:text-5xl">{t}</span>
          <Sparkles className="h-5 w-5 text-electric/70" />
        </span>
      ))}
    </Marquee>
  </section>
);

/* ---------------- What Kyvelra Does — numbered manifesto ---------------- */
const CHAPTERS = [
  {
    n: "01",
    title: "Find the candidates others miss",
    body: "Describe the role in plain language. Kyvelra translates it into precise search intent and surfaces qualified people across the open web — not just the ones already applying.",
    icon: Search,
  },
  {
    n: "02",
    title: "Turn research into strategy",
    body: "Generate sourcing strategies, target-company lists, and messaging angles in seconds. Kyvelra reasons over the market so you walk into every search with a plan.",
    icon: Target,
  },
  {
    n: "03",
    title: "Automate the busywork",
    body: "Boolean strings, profile summaries, screening notes, and follow-up drafts are handled for you. The repetitive research disappears; the judgment stays yours.",
    icon: Workflow,
  },
  {
    n: "04",
    title: "Move at the speed of hiring",
    body: "Collapse hours of manual sourcing into minutes. Spend the time you save on conversations that actually close candidates.",
    icon: Gauge,
  },
];

export const WhatKyvelra = () => (
  <section id="what" className="bg-background py-24 sm:py-32" data-testid="what-kyvelra">
    <div className={container}>
      <Reveal className="max-w-2xl">
        <Eyebrow>What Kyvelra does</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
          A recruiting co-pilot in four movements.
        </h2>
      </Reveal>

      <div className="mt-16 grid gap-x-16 lg:grid-cols-[0.4fr_1fr]">
        <div className="hidden lg:block">
          <div className="sticky top-28">
            <p className="font-serif text-[10rem] leading-none tracking-tighter text-electric/10">
              04
            </p>
            <p className="-mt-4 max-w-[16rem] font-display text-sm text-muted-foreground">
              Every chapter maps to a stage of your workflow — discover, strategize, automate, accelerate.
            </p>
          </div>
        </div>

        <div className="flex flex-col divide-y divide-border">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.05}>
              <div className="group flex gap-6 py-8 sm:gap-10 sm:py-10">
                <span className="font-serif text-2xl italic text-electric sm:text-3xl">{c.n}</span>
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-serif text-2xl tracking-tight text-navy-950 sm:text-3xl">
                      {c.title}
                    </h3>
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-border text-electric transition-colors duration-300 group-hover:border-electric group-hover:bg-electric group-hover:text-white">
                      <c.icon className="h-5 w-5" />
                    </span>
                  </div>
                  <p className="mt-3 max-w-xl font-body text-base leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* Reusable clipped image frame */
const Frame = ({ src, alt, className = "" }) => (
  <div className={`relative overflow-hidden rounded-3xl border border-border ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="h-full w-full object-cover" />
    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/10" />
  </div>
);

/* ---------------- Candidate Discovery ---------------- */
export const CandidateDiscovery = () => (
  <section id="discovery" className="bg-muted/40 py-24 sm:py-32" data-testid="candidate-discovery">
    <div className={`${container} grid items-center gap-14 lg:grid-cols-2`}>
      <Reveal className="order-2 lg:order-1">
        <Eyebrow>Candidate discovery</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
          Search by intent, not keywords.
        </h2>
        <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-muted-foreground">
          Tell Kyvelra who you're looking for the way you'd describe it to a
          colleague. It maps skills, adjacencies, and career signals to surface
          a shortlist you can actually work — ranked, explained, and de-duplicated.
        </p>
        <ul className="mt-8 space-y-4">
          {[
            { i: Layers, t: "Semantic matching across 40+ profile signals" },
            { i: ShieldCheck, t: "Bias-aware ranking with transparent reasoning" },
            { i: ArrowUpRight, t: "One-click enrichment and outreach-ready summaries" },
          ].map((f) => (
            <li key={f.t} className="flex items-center gap-4">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-electric/10 text-electric">
                <f.i className="h-5 w-5" />
              </span>
              <span className="font-display text-sm text-navy-900 sm:text-base">{f.t}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1} className="order-1 lg:order-2">
        <div className="relative">
          <div className="spotlight absolute -inset-8 -z-10" />
          <Frame src={IMG.network} alt="Abstract candidate discovery network" className="aspect-[4/3] animate-float-slow" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- AI Recruiting Intelligence ---------------- */
export const AIIntelligence = () => {
  const cards = [
    { i: BrainCircuit, t: "Reasoning engine", d: "Understands nuanced role requirements and market context." },
    { i: Cpu, t: "Research automation", d: "Compiles company, team, and candidate research on demand." },
    { i: Target, t: "Strategy generation", d: "Drafts sourcing plans, target lists, and outreach angles." },
    { i: Scale, t: "Signal scoring", d: "Weighs recency, relevance, and fit into a clear ranking." },
  ];
  return (
    <section id="intelligence" className="relative overflow-hidden bg-navy-950 py-24 sm:py-32 grain" data-testid="ai-intelligence">
      <div className="pointer-events-none absolute right-0 top-0 h-[50vh] w-[50vh] rounded-full bg-[radial-gradient(circle,rgba(47,107,255,0.25),transparent_60%)] blur-3xl" />
      <div className={`${container} relative z-10`}>
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow light>AI recruiting intelligence</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
              Intelligence that reasons like a
              <span className="italic text-electric-bright"> senior sourcer.</span>
            </h2>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-electric-ice/60">
              Under the hood, Kyvelra chains research, ranking, and generation so
              every result comes with context — why this candidate, why now, and
              what to say next.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Frame src={IMG.grid} alt="Abstract data intelligence grid" className="aspect-[16/11]" />
          </Reveal>
        </div>

        <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.07}>
              <div className="glass-dark h-full rounded-3xl p-6 transition-transform duration-300 hover:-translate-y-1">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-electric/15 text-electric-bright">
                  <c.i className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-white">{c.t}</h3>
                <p className="mt-2 font-body text-sm leading-relaxed text-electric-ice/55">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Recruit Faster ---------------- */
export const RecruitFaster = () => {
  const steps = [
    { t: "Describe the role", d: "Plain-language brief in seconds." },
    { t: "Kyvelra sources", d: "Candidates + strategy generated live." },
    { t: "Review & refine", d: "Ranked shortlist with reasoning." },
    { t: "Reach out", d: "Personalized drafts, ready to send." },
  ];
  return (
    <section className="bg-background py-24 sm:py-32" data-testid="recruit-faster">
      <div className={container}>
        <Reveal className="max-w-2xl">
          <Eyebrow>Recruit faster</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
            Hours of sourcing, compressed into minutes.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <Reveal key={s.t} delay={i * 0.08}>
              <div className="group relative rounded-3xl border border-border bg-background p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(15,30,70,0.35)]">
                <span className="font-serif text-4xl italic text-electric/40">0{i + 1}</span>
                <h3 className="mt-4 font-display text-lg font-semibold text-navy-950">{s.t}</h3>
                <p className="mt-2 font-body text-sm text-muted-foreground">{s.d}</p>
                {i < steps.length - 1 && (
                  <span className="absolute right-6 top-8 hidden text-electric/30 lg:block">→</span>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Industries Served ---------------- */
export const Industries = () => {
  const items = [
    { i: Cpu, t: "Technology & SaaS" },
    { i: HeartPulse, t: "Healthcare & Life Sciences" },
    { i: Landmark, t: "Finance & Fintech" },
    { i: Building2, t: "Staffing & RPO" },
    { i: Rocket, t: "Startups & Scale-ups" },
    { i: Users, t: "Executive Search" },
  ];
  return (
    <section id="industries" className="bg-muted/40 py-24 sm:py-32" data-testid="industries">
      <div className={container}>
        <Reveal className="max-w-2xl">
          <Eyebrow>Industries served</Eyebrow>
          <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
            Built for the way your desk works.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => (
            <Reveal key={it.t} delay={i * 0.05} className="bg-background">
              <div className="group flex items-center gap-4 p-8 transition-colors duration-300 hover:bg-electric/5">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-electric/10 text-electric transition-transform duration-300 group-hover:scale-110">
                  <it.i className="h-5 w-5" />
                </span>
                <span className="font-display text-lg font-medium text-navy-950">{it.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- Founding Recruiter Program ---------------- */
export const FoundingProgram = () => {
  const perks = [
    "Lifetime founding-member pricing",
    "Direct line to the product team",
    "Shape the roadmap with your workflows",
    "Early access to every new capability",
  ];
  return (
    <section id="founding" className="relative overflow-hidden bg-navy-950 py-24 sm:py-32 grain" data-testid="founding-program">
      <div className="pointer-events-none absolute inset-0">
        <img src={IMG.waves} alt="" aria-hidden className="h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/85 to-navy-950" />
      </div>
      <div className={`${container} relative z-10`}>
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          <Reveal>
            <Eyebrow light>Founding recruiter program</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
              Become a Founding Recruiter.
            </h2>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-electric-ice/65">
              We're partnering with a small group of recruiters to build Kyvelra
              in the open. Join early, and help define the product the rest of the
              industry will use.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {perks.map((p) => (
                <li key={p} className="flex items-start gap-3 font-display text-sm text-electric-ice/80">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-electric-bright" />
                  {p}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="glass-dark rounded-3xl p-8" id="founding-form">
              <h3 className="font-serif text-2xl text-white">Apply for a founding seat</h3>
              <p className="mt-2 font-body text-sm text-electric-ice/60">
                Limited cohort. We review every application personally.
              </p>
              <div className="mt-6">
                <WaitlistForm interest="founding" dark compact={false} testid="founding-waitlist" cta="Become a Founding Recruiter" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- About ---------------- */
export const About = () => (
  <section id="about" className="bg-background py-24 sm:py-32" data-testid="about">
    <div className={`${container} grid gap-14 lg:grid-cols-[1fr_0.8fr] lg:items-center`}>
      <Reveal>
        <Eyebrow>About Kyvelra</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl leading-tight tracking-tight text-navy-950 sm:text-5xl">
          We believe great hiring starts with
          <span className="italic text-electric"> better discovery.</span>
        </h2>
        <p className="mt-6 max-w-xl font-body text-base leading-relaxed text-muted-foreground">
          Kyvelra is on a mission to give every recruiter the reach of an entire
          research team. We pair frontier AI with the craft of experienced
          sourcers to make finding the right person feel effortless — and
          genuinely human where it counts.
        </p>
        <p className="mt-4 max-w-xl font-body text-base leading-relaxed text-muted-foreground">
          Private, secure, and built for enterprise from day one.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="relative">
          <div className="spotlight absolute -inset-6 -z-10" />
          <Frame src={IMG.plexus} alt="Abstract network of nodes" className="aspect-square" />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Email Signup ---------------- */
export const Signup = () => (
  <section id="signup" className="bg-background pb-24 sm:pb-32" data-testid="signup">
    <div className={container}>
      <Reveal>
        <div className="relative overflow-hidden rounded-[2.5rem] bg-navy-950 px-8 py-16 text-center grain sm:px-16 sm:py-24">
          <div className="pointer-events-none absolute left-1/2 top-0 h-[40vh] w-[40vh] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(47,107,255,0.3),transparent_60%)] blur-3xl" />
          <div className="dot-grid absolute inset-0 opacity-30" />
          <div className="relative z-10 mx-auto max-w-2xl">
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-white sm:text-5xl">
              Recruit smarter, starting now.
            </h2>
            <p className="mx-auto mt-5 max-w-md font-body text-base text-electric-ice/65">
              Join the beta waitlist and be first to source with Kyvelra.
            </p>
            <div className="mx-auto mt-9 max-w-xl">
              <WaitlistForm interest="beta" dark testid="signup-waitlist" cta="Join the Beta Waitlist" />
            </div>
            <p className="mt-4 font-display text-xs text-electric-ice/40">
              No spam. Just an invite when your seat opens up.
            </p>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
